import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Trophy,
  Medal,
  Crown,
  Sparkles,
  Target,
  Users,
  TrendingUp,
  Zap,
  Award,
  Flame,
  Star,
  Wallet,
  Briefcase,
} from 'lucide-react';
import { authFetch } from '../services/auth';

interface LeaderboardItem {
  user_id: string;
  nickname: string;
  avatar: string;
  job_type: string;
  job_level: string;
  interview_type: string;
  interviewer_id: string;
  score: number;
  created_at: number;
}

interface SalaryNegotiationItem {
  id: number;
  userId: string;
  nickname: string;
  avatar?: string;
  jobType: string;
  jobLevel: string;
  finalOffer: number;
  isAccepted: number;
  totalRounds: number;
  score: number;
  createdAt: number;
}

type TabType = 'interview' | 'salary';

// 根据评分获取颜色和等级信息 - 与简历评估结果页保持一致
const getScoreInfo = (score: number) => {
  if (score >= 90) {
    return {
      color: '#22c55e',
      gradient: 'from-green-400 via-green-500 to-green-600',
      label: '卓越',
      glow: 'shadow-green-500/30',
      textColor: 'text-green-400',
    };
  }
  if (score >= 80) {
    return {
      color: '#f59e0b',
      gradient: 'from-amber-400 via-amber-500 to-amber-600',
      label: '优秀',
      glow: 'shadow-amber-500/30',
      textColor: 'text-amber-400',
    };
  }
  if (score >= 70) {
    return {
      color: '#eab308',
      gradient: 'from-yellow-400 via-yellow-500 to-yellow-600',
      label: '良好',
      glow: 'shadow-yellow-500/30',
      textColor: 'text-yellow-400',
    };
  }
  if (score >= 60) {
    return {
      color: '#f97316',
      gradient: 'from-orange-400 via-orange-500 to-orange-600',
      label: '及格',
      glow: 'shadow-orange-500/30',
      textColor: 'text-orange-400',
    };
  }
  return {
    color: '#ef4444',
    gradient: 'from-red-400 via-red-500 to-red-600',
    label: '待提升',
    glow: 'shadow-red-500/30',
    textColor: 'text-red-400',
  };
};

// 前三名特殊样式配置
const getTopThreeStyle = (rank: number) => {
  const styles = {
    1: {
      // 金牌
      container:
        'bg-gradient-to-br from-yellow-500/10 via-amber-500/5 to-yellow-600/10 border-yellow-500/40',
      badge: 'bg-gradient-to-r from-yellow-400 to-amber-500 text-yellow-950',
      iconBg:
        'bg-gradient-to-br from-yellow-400 via-amber-400 to-yellow-500 shadow-lg shadow-yellow-500/40',
      crown: 'text-yellow-400',
      rankBg:
        'bg-gradient-to-br from-yellow-400 to-amber-500 text-yellow-950 shadow-lg shadow-yellow-500/30',
      glow: 'shadow-yellow-500/20',
      border: 'border-yellow-500/30',
      shimmer:
        'before:bg-gradient-to-r before:from-transparent before:via-yellow-400/20 before:to-transparent',
      avatarGlow: 'from-yellow-400 via-amber-400 to-yellow-500',
      avatarBorder: 'from-yellow-400 to-amber-500',
    },
    2: {
      // 银牌
      container:
        'bg-gradient-to-br from-slate-400/10 via-gray-400/5 to-slate-500/10 border-slate-400/40',
      badge: 'bg-gradient-to-r from-slate-300 to-gray-400 text-slate-950',
      iconBg:
        'bg-gradient-to-br from-slate-300 via-gray-300 to-slate-400 shadow-lg shadow-slate-400/40',
      crown: 'text-slate-300',
      rankBg:
        'bg-gradient-to-br from-slate-300 to-gray-400 text-slate-950 shadow-lg shadow-slate-400/30',
      glow: 'shadow-slate-400/20',
      border: 'border-slate-400/30',
      shimmer:
        'before:bg-gradient-to-r before:from-transparent before:via-slate-300/20 before:to-transparent',
      avatarGlow: 'from-slate-300 via-gray-300 to-slate-400',
      avatarBorder: 'from-slate-300 to-gray-400',
    },
    3: {
      // 铜牌
      container:
        'bg-gradient-to-br from-orange-600/10 via-amber-700/5 to-orange-700/10 border-orange-600/40',
      badge: 'bg-gradient-to-r from-orange-500 to-amber-600 text-orange-950',
      iconBg:
        'bg-gradient-to-br from-orange-500 via-amber-500 to-orange-600 shadow-lg shadow-orange-500/40',
      crown: 'text-orange-500',
      rankBg:
        'bg-gradient-to-br from-orange-500 to-amber-600 text-orange-950 shadow-lg shadow-orange-500/30',
      glow: 'shadow-orange-500/20',
      border: 'border-orange-600/30',
      shimmer:
        'before:bg-gradient-to-r before:from-transparent before:via-orange-500/20 before:to-transparent',
      avatarGlow: 'from-orange-500 via-amber-500 to-orange-600',
      avatarBorder: 'from-orange-500 to-amber-600',
    },
  };
  return (
    styles[rank as keyof typeof styles] || {
      container: 'bg-slate-800/40 border-slate-700/30',
      badge: 'bg-slate-700 text-slate-300',
      iconBg: 'bg-slate-700',
      crown: 'text-slate-500',
      rankBg: 'bg-slate-700 text-slate-300',
      glow: '',
      border: 'border-slate-700/30',
      shimmer: '',
    }
  );
};

// 评分圆环组件 - 增强版
const ScoreRing = ({
  score,
  size = 56,
  strokeWidth = 4,
  animated = true,
}: {
  score: number;
  size?: number;
  strokeWidth?: number;
  animated?: boolean;
}) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (score / 100) * circumference;
  const scoreInfo = getScoreInfo(score);

  return (
    <div
      className="relative flex items-center justify-center"
      style={{ width: size, height: size }}
    >
      {/* 外发光效果 */}
      <div
        className={`absolute inset-0 rounded-full blur-md opacity-50 bg-gradient-to-br ${scoreInfo.gradient}`}
      />

      <svg
        className="w-full h-full transform -rotate-90 relative z-10"
        style={{ width: size, height: size }}
      >
        {/* 背景圆环 */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="#1F2937"
          strokeWidth={strokeWidth}
          fill="none"
        />
        {/* 进度圆环 */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={scoreInfo.color}
          strokeWidth={strokeWidth}
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={animated ? strokeDashoffset : strokeDashoffset}
          strokeLinecap="round"
          className="transition-all duration-1000 ease-out"
        />
      </svg>

      {/* 中心内容 */}
      <div className="absolute inset-0 flex flex-col items-center justify-center z-20">
        <span className={`text-lg font-bold leading-none ${scoreInfo.textColor}`}>
          {score}
        </span>
      </div>
    </div>
  );
};

// 前三名展示卡片组件 - 面试榜
const TopThreeCard = ({
  item,
  rank,
  index,
}: {
  item: LeaderboardItem;
  rank: number;
  index: number;
}) => {
  const style = getTopThreeStyle(rank);
  const scoreInfo = getScoreInfo(item.score);

  const icons = {
    1: <Crown className="w-6 h-6 text-yellow-950" />,
    2: <Medal className="w-6 h-6 text-slate-950" />,
    3: <Award className="w-6 h-6 text-orange-950" />,
  };

  return (
    <div
      className={`relative overflow-hidden rounded-2xl border-2 ${style.container} ${style.border} ${style.glow} p-6 transition-all duration-500 hover:scale-[1.02] hover:shadow-2xl group`}
      style={{
        animationDelay: `${index * 150}ms`,
      }}
    >
      {/* 闪光效果 */}
      <div
        className={`absolute inset-0 ${style.shimmer} before:absolute before:inset-0 before:-translate-x-full group-hover:before:translate-x-full before:transition-transform before:duration-1000`}
      />

      {/* 排名标识 */}
      <div className="absolute -top-1 -right-1">
        <div
          className={`w-12 h-12 ${style.rankBg} rounded-bl-2xl rounded-tr-xl flex items-center justify-center font-bold text-xl`}
        >
          {rank}
        </div>
      </div>

      <div className="flex flex-col items-center text-center relative z-10">
        {/* 头像区域 */}
        <div className="relative mb-4">
          {/* 头像光环 */}
          <div
            className={`absolute inset-0 rounded-full blur-md bg-gradient-to-br ${style.avatarGlow} opacity-60 scale-110`}
          />

          {/* 头像 */}
          <div
            className={`relative w-20 h-20 rounded-full p-1 bg-gradient-to-br ${style.avatarBorder}`}
          >
            <img
              src={item.avatar}
              alt={item.nickname}
              className="w-full h-full rounded-full object-cover border-2 border-slate-900"
            />
          </div>

          {/* 排名图标 */}
          <div
            className={`absolute -bottom-1 -right-1 w-8 h-8 ${style.iconBg} rounded-full flex items-center justify-center shadow-lg`}
          >
            {icons[rank as keyof typeof icons]}
          </div>
        </div>

        {/* 用户信息 */}
        <h3 className="text-white font-bold text-lg mb-1 truncate max-w-[200px]">
          {item.nickname}
        </h3>

        {/* 职位信息 */}
        <div className="flex items-center gap-2 mb-3">
          <span className="text-slate-400 text-sm">{item.job_type}</span>
          <span className="w-1 h-1 rounded-full bg-slate-600" />
          <span className="px-2 py-0.5 rounded-full bg-slate-700/50 text-slate-300 text-xs">
            {item.job_level}
          </span>
        </div>

        {/* 评分展示 */}
        <div className="flex items-center gap-3">
          <ScoreRing score={item.score} size={64} strokeWidth={5} />
          <div className="text-left">
            <div
              className={`text-sm font-bold bg-gradient-to-r ${scoreInfo.gradient} bg-clip-text text-transparent`}
            >
              {scoreInfo.label}
            </div>
            <div className="text-xs text-slate-500">{item.interview_type}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

// 前三名展示卡片组件 - 谈薪榜
const TopThreeSalaryCard = ({
  item,
  rank,
  index,
}: {
  item: SalaryNegotiationItem;
  rank: number;
  index: number;
}) => {
  const style = getTopThreeStyle(rank);
  const scoreInfo = getScoreInfo(item.score);

  const icons = {
    1: <Crown className="w-6 h-6 text-yellow-950" />,
    2: <Medal className="w-6 h-6 text-slate-950" />,
    3: <Award className="w-6 h-6 text-orange-950" />,
  };

  return (
    <div
      className={`relative overflow-hidden rounded-2xl border-2 ${style.container} ${style.border} ${style.glow} p-6 transition-all duration-500 hover:scale-[1.02] hover:shadow-2xl group`}
      style={{
        animationDelay: `${index * 150}ms`,
      }}
    >
      {/* 闪光效果 */}
      <div
        className={`absolute inset-0 ${style.shimmer} before:absolute before:inset-0 before:-translate-x-full group-hover:before:translate-x-full before:transition-transform before:duration-1000`}
      />

      {/* 排名标识 */}
      <div className="absolute -top-1 -right-1">
        <div
          className={`w-12 h-12 ${style.rankBg} rounded-bl-2xl rounded-tr-xl flex items-center justify-center font-bold text-xl`}
        >
          {rank}
        </div>
      </div>

      <div className="flex flex-col items-center text-center relative z-10">
        {/* 头像区域 */}
        <div className="relative mb-4">
          {/* 头像光环 */}
          <div
            className={`absolute inset-0 rounded-full blur-md bg-gradient-to-br ${style.avatarGlow} opacity-60 scale-110`}
          />

          {/* 头像 */}
          <div
            className={`relative w-20 h-20 rounded-full p-1 bg-gradient-to-br ${style.avatarBorder}`}
          >
            <img
              src={
                item.avatar ||
                'https://api.dicebear.com/7.x/avataaars/svg?seed=' + item.userId
              }
              alt={item.nickname}
              className="w-full h-full rounded-full object-cover border-2 border-slate-900"
            />
          </div>

          {/* 排名图标 */}
          <div
            className={`absolute -bottom-1 -right-1 w-8 h-8 ${style.iconBg} rounded-full flex items-center justify-center shadow-lg`}
          >
            {icons[rank as keyof typeof icons]}
          </div>
        </div>

        {/* 用户信息 */}
        <h3 className="text-white font-bold text-lg mb-1 truncate max-w-[200px]">
          {item.nickname || '匿名用户'}
        </h3>

        {/* 职位信息 */}
        <div className="flex items-center gap-2 mb-3">
          <span className="text-slate-400 text-sm">{item.jobType}</span>
          <span className="w-1 h-1 rounded-full bg-slate-600" />
          <span className="px-2 py-0.5 rounded-full bg-slate-700/50 text-slate-300 text-xs">
            {item.jobLevel}
          </span>
        </div>

        {/* 评分和offer展示 */}
        <div className="flex items-center gap-3">
          <ScoreRing score={Math.round(item.score)} size={64} strokeWidth={5} />
          <div className="text-left">
            <div
              className={`text-sm font-bold bg-gradient-to-r ${scoreInfo.gradient} bg-clip-text text-transparent`}
            >
              {scoreInfo.label}
            </div>
            <div
              className={
                item.isAccepted
                  ? 'text-xs text-green-400'
                  : 'text-xs text-red-400'
              }
            >
              {item.isAccepted ? `${item.finalOffer}k` : '未达成'}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// 列表项组件 - 面试榜
const LeaderboardListItem = ({
  item,
  rank,
  index,
}: {
  item: LeaderboardItem;
  rank: number;
  index: number;
}) => {
  const scoreInfo = getScoreInfo(item.score);

  return (
    <div
      className="group relative p-4 bg-slate-800/30 hover:bg-slate-700/40 border border-slate-700/20
                 hover:border-slate-600/40 rounded-xl transition-all duration-300
                 hover:shadow-lg hover:shadow-black/20 hover:-translate-y-0.5"
      style={{
        animationDelay: `${(index + 3) * 50}ms`,
      }}
    >
      <div className="flex items-center gap-4">
        {/* 排名 */}
        <div className="flex-shrink-0 w-10 flex justify-center">
          {rank <= 10 ? (
            <div
              className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold text-sm
              ${
                rank <= 3
                  ? 'bg-gradient-to-br from-yellow-400/20 to-amber-500/20 text-yellow-400 border border-yellow-500/30'
                  : 'bg-slate-700/50 text-slate-400 border border-slate-600/30'
              }`}
            >
              {rank}
            </div>
          ) : (
            <span className="text-slate-500 font-medium w-8 text-center">
              {rank}
            </span>
          )}
        </div>

        {/* 头像 */}
        <div className="flex-shrink-0 relative">
          <div
            className={`absolute inset-0 rounded-full blur-sm bg-gradient-to-br ${scoreInfo.gradient} opacity-40`}
          />
          <img
            src={item.avatar}
            alt={item.nickname}
            className="relative w-11 h-11 rounded-full object-cover border-2 border-slate-700 group-hover:border-slate-500 transition-colors"
          />
          {rank <= 3 && (
            <div className="absolute -top-1 -right-1">
              <Flame className="w-4 h-4 text-orange-500 fill-orange-500" />
            </div>
          )}
        </div>

        {/* 用户信息 */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-white font-semibold truncate transition-colors">
              {item.nickname}
            </span>
            {rank <= 10 && (
              <span
                className={`px-1.5 py-0.5 rounded text-[10px] font-bold bg-gradient-to-r ${scoreInfo.gradient} text-gray-900`}
              >
                TOP{rank}
              </span>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-400">
            <span className="text-slate-300">{item.job_type}</span>
            <span className="w-1 h-1 rounded-full bg-slate-600" />
            <span className="px-1.5 py-0.5 rounded bg-slate-700/50 text-slate-300">
              {item.job_level}
            </span>
            <span className="w-1 h-1 rounded-full bg-slate-600" />
            <span>{item.interview_type}</span>
          </div>
        </div>

        {/* 评分 */}
        <div className="flex-shrink-0 flex items-center gap-3">
          <ScoreRing score={item.score} size={48} strokeWidth={3} />
          <div className="hidden sm:block text-right min-w-[60px]">
            <div
              className={`text-xs font-bold bg-gradient-to-r ${scoreInfo.gradient} bg-clip-text text-transparent`}
            >
              {scoreInfo.label}
            </div>
            <div className="text-[10px] text-slate-500">综合评分</div>
          </div>
        </div>
      </div>
    </div>
  );
};

// 列表项组件 - 谈薪榜
const SalaryLeaderboardListItem = ({
  item,
  rank,
  index,
}: {
  item: SalaryNegotiationItem;
  rank: number;
  index: number;
}) => {
  const scoreInfo = getScoreInfo(item.score);

  return (
    <div
      className="group relative p-4 bg-slate-800/30 hover:bg-slate-700/40 border border-slate-700/20
                 hover:border-slate-600/40 rounded-xl transition-all duration-300
                 hover:shadow-lg hover:shadow-black/20 hover:-translate-y-0.5"
      style={{
        animationDelay: `${(index + 3) * 50}ms`,
      }}
    >
      <div className="flex items-center gap-4">
        {/* 排名 */}
        <div className="flex-shrink-0 w-10 flex justify-center">
          {rank <= 10 ? (
            <div
              className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold text-sm
              ${
                rank <= 3
                  ? 'bg-gradient-to-br from-yellow-400/20 to-amber-500/20 text-yellow-400 border border-yellow-500/30'
                  : 'bg-slate-700/50 text-slate-400 border border-slate-600/30'
              }`}
            >
              {rank}
            </div>
          ) : (
            <span className="text-slate-500 font-medium w-8 text-center">
              {rank}
            </span>
          )}
        </div>

        {/* 头像 */}
        <div className="flex-shrink-0 relative">
          <div
            className={`absolute inset-0 rounded-full blur-sm bg-gradient-to-br ${scoreInfo.gradient} opacity-40`}
          />
          <img
            src={
              item.avatar ||
              'https://api.dicebear.com/7.x/avataaars/svg?seed=' + item.userId
            }
            alt={item.nickname}
            className="relative w-11 h-11 rounded-full object-cover border-2 border-slate-700 group-hover:border-slate-500 transition-colors"
          />
          {rank <= 3 && (
            <div className="absolute -top-1 -right-1">
              <Flame className="w-4 h-4 text-orange-500 fill-orange-500" />
            </div>
          )}
        </div>

        {/* 用户信息 */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-white font-semibold truncate transition-colors">
              {item.nickname || '匿名用户'}
            </span>
            {rank <= 10 && (
              <span
                className={`px-1.5 py-0.5 rounded text-[10px] font-bold bg-gradient-to-r ${scoreInfo.gradient} text-gray-900`}
              >
                TOP{rank}
              </span>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-400">
            <span className="text-slate-300">{item.jobType}</span>
            <span className="w-1 h-1 rounded-full bg-slate-600" />
            <span className="px-1.5 py-0.5 rounded bg-slate-700/50 text-slate-300">
              {item.jobLevel}
            </span>
            <span className="w-1 h-1 rounded-full bg-slate-600" />
            <span
              className={item.isAccepted ? 'text-green-400' : 'text-red-400'}
            >
              {item.isAccepted ? `${item.finalOffer}k` : '未达成'}
            </span>
          </div>
        </div>

        {/* 评分 */}
        <div className="flex-shrink-0 flex items-center gap-3">
          <ScoreRing score={item.score} size={48} strokeWidth={3} />
          <div className="hidden sm:block text-right min-w-[60px]">
            <div
              className={`text-xs font-bold bg-gradient-to-r ${scoreInfo.gradient} bg-clip-text text-transparent`}
            >
              {scoreInfo.label}
            </div>
            <div className="text-[10px] text-slate-500">
              {item.totalRounds}轮
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// 统计卡片组件
const StatCard = ({
  icon: Icon,
  label,
  value,
  color,
}: {
  icon: React.ElementType;
  label: string;
  value: string | number;
  color: string;
}) => (
  <div className="bg-slate-800/40 backdrop-blur-sm border border-slate-700/30 rounded-xl p-4 flex items-center gap-3 hover:bg-slate-700/40 transition-colors">
    <div
      className={`w-10 h-10 rounded-lg flex items-center justify-center ${color}`}
    >
      <Icon className="w-5 h-5 text-white" />
    </div>
    <div>
      <div className="text-slate-400 text-xs">{label}</div>
      <div className="text-white font-bold text-lg">{value}</div>
    </div>
  </div>
);

// 加载骨架屏组件
const SkeletonCard = () => (
  <div className="bg-slate-800/30 border border-slate-700/20 rounded-xl p-6 animate-pulse">
    <div className="flex flex-col items-center">
      <div className="w-20 h-20 rounded-full bg-slate-700/50 mb-4" />
      <div className="w-24 h-4 bg-slate-700/50 rounded mb-2" />
      <div className="w-16 h-3 bg-slate-700/50 rounded mb-4" />
      <div className="w-14 h-14 rounded-full bg-slate-700/50" />
    </div>
  </div>
);

const SkeletonListItem = () => (
  <div className="bg-slate-800/30 border border-slate-700/20 rounded-xl p-4 animate-pulse">
    <div className="flex items-center gap-4">
      <div className="w-8 h-8 rounded-lg bg-slate-700/50" />
      <div className="w-11 h-11 rounded-full bg-slate-700/50" />
      <div className="flex-1">
        <div className="w-24 h-4 bg-slate-700/50 rounded mb-2" />
        <div className="w-32 h-3 bg-slate-700/50 rounded" />
      </div>
      <div className="w-12 h-12 rounded-full bg-slate-700/50" />
    </div>
  </div>
);

export default function LeaderboardPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabType>('interview');

  // 面试排行榜数据
  const [leaderboard, setLeaderboard] = useState<LeaderboardItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const hasFetched = useRef(false);

  // 谈薪排行榜数据
  const [salaryLeaderboard, setSalaryLeaderboard] = useState<
    SalaryNegotiationItem[]
  >([]);
  const [salaryLoading, setSalaryLoading] = useState(true);
  const [salaryError, setSalaryError] = useState('');
  const salaryHasFetched = useRef(false);

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    fetchLeaderboard();
  }, []);

  useEffect(() => {
    if (salaryHasFetched.current) return;
    salaryHasFetched.current = true;
    fetchSalaryLeaderboard();
  }, []);

  const fetchLeaderboard = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await authFetch(
        `${import.meta.env.VITE_API_BASE_URL || ''}/api/leaderboard`,
      );

      if (!response.ok) {
        throw new Error('获取排行榜失败');
      }

      const data = await response.json();
      setLeaderboard(data.leaderboard || []);
    } catch (err) {
      setError('获取排行榜失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  const fetchSalaryLeaderboard = async () => {
    setSalaryLoading(true);
    setSalaryError('');

    try {
      const response = await authFetch(
        `${import.meta.env.VITE_API_BASE_URL || ''}/api/salary-negotiations/leaderboard`,
      );

      if (!response.ok) {
        throw new Error('获取谈薪排行榜失败');
      }

      const data = await response.json();
      setSalaryLeaderboard(data.leaderboard || []);
    } catch (err) {
      setSalaryError('获取谈薪排行榜失败，请稍后重试');
    } finally {
      setSalaryLoading(false);
    }
  };

  // 计算统计数据 - 面试榜
  const interviewStats = {
    totalUsers: leaderboard.length,
    avgScore:
      leaderboard.length > 0
        ? Math.round(
            leaderboard.reduce((sum, item) => sum + item.score, 0) /
              leaderboard.length,
          )
        : 0,
    topScore: leaderboard.length > 0 ? leaderboard[0].score : 0,
    excellentCount: leaderboard.filter((item) => item.score >= 90).length,
  };

  // 计算统计数据 - 谈薪榜
  const salaryStats = {
    totalUsers: salaryLeaderboard.length,
    avgScore:
      salaryLeaderboard.length > 0
        ? Math.round(
            salaryLeaderboard.reduce((sum, item) => sum + item.score, 0) /
              salaryLeaderboard.length,
          )
        : 0,
    topScore:
      salaryLeaderboard.length > 0 ? Math.round(salaryLeaderboard[0].score) : 0,
    totalOffers: salaryLeaderboard.filter((item) => item.isAccepted).length,
  };

  // 分割前三名和其余数据
  const topThree = leaderboard.slice(0, 3);
  const restList = leaderboard.slice(3);

  const salaryTopThree = salaryLeaderboard.slice(0, 3);
  const salaryRestList = salaryLeaderboard.slice(3);

  const isLoading = activeTab === 'interview' ? loading : salaryLoading;
  const hasError = activeTab === 'interview' ? error : salaryError;
  const currentData =
    activeTab === 'interview' ? leaderboard : salaryLeaderboard;
  const currentStats = activeTab === 'interview' ? interviewStats : salaryStats;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0B0F19] via-[#0F172A] to-[#1E293B]">
      {/* 背景装饰 */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-amber-500/5 rounded-full blur-3xl" />
        <div className="absolute top-40 right-20 w-96 h-96 bg-emerald-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-1/3 w-80 h-80 bg-amber-500/5 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 max-w-5xl mx-auto px-4 pt-24 pb-12">
        {/* 标题 */}
        <div className="relative mb-8">
          <h1 className="text-2xl font-bold text-white text-center">排行榜</h1>
        </div>

        {/* Tab切换 */}
        <div className="flex justify-center mb-8">
          <div className="inline-flex bg-slate-800/60 backdrop-blur-sm rounded-xl p-1 border border-slate-700/50">
            <button
              onClick={() => setActiveTab('interview')}
              className={`px-6 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 cursor-pointer ${
                activeTab === 'interview'
                  ? 'bg-gradient-to-r from-amber-500 to-amber-600 text-white shadow-lg shadow-amber-500/25'
                  : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
              }`}
            >
              面试排行
            </button>
            <button
              onClick={() => setActiveTab('salary')}
              className={`px-6 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 cursor-pointer ${
                activeTab === 'salary'
                  ? 'bg-gradient-to-r from-amber-500 to-amber-600 text-white shadow-lg shadow-amber-500/25'
                  : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
              }`}
            >
              谈薪排行
            </button>
          </div>
        </div>

        {/* 统计卡片 */}
        {!isLoading && !hasError && currentData.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <StatCard
              icon={Users}
              label="上榜人数"
              value={currentStats.totalUsers}
              color="bg-gradient-to-br from-amber-500 to-amber-600"
            />
            <StatCard
              icon={Target}
              label="平均分数"
              value={currentStats.avgScore}
              color="bg-gradient-to-br from-emerald-500 to-emerald-600"
            />
            <StatCard
              icon={TrendingUp}
              label="最高分数"
              value={currentStats.topScore}
              color="bg-gradient-to-br from-amber-500 to-orange-500"
            />
            <StatCard
              icon={activeTab === 'interview' ? Star : Wallet}
              label={activeTab === 'interview' ? '卓越人数' : '达成人数'}
              value={
                activeTab === 'interview'
                  ? currentStats.excellentCount
                  : salaryStats.totalOffers
              }
              color={
                activeTab === 'interview'
                  ? 'bg-gradient-to-br from-amber-400 to-yellow-500'
                  : 'bg-gradient-to-br from-emerald-400 to-emerald-500'
              }
            />
          </div>
        )}

        {/* 内容区域 */}
        <div className="space-y-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-12 h-12 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : hasError ? (
            <div className="bg-slate-800/50 backdrop-blur-xl rounded-2xl border border-slate-700/30 p-12">
              <div className="text-center">
                <button
                  onClick={() =>
                    activeTab === 'interview'
                      ? fetchLeaderboard()
                      : fetchSalaryLeaderboard()
                  }
                  className="inline-flex items-center gap-2 text-slate-400 hover:text-white transition-colors cursor-pointer"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                  重新加载
                </button>
              </div>
            </div>
          ) : currentData.length === 0 ? (
            <div className="bg-slate-800/50 backdrop-blur-xl rounded-2xl border border-slate-700/30 p-16">
              <div className="text-center">
                {/* 空状态 - 放大镜（无数据） */}
                <div className="relative mx-auto mb-6 w-28 h-28">
                  <div className="absolute inset-0 flex items-center justify-center">
                    <svg
                      width="64"
                      height="64"
                      viewBox="0 0 64 64"
                      fill="none"
                      className="text-[#5a6280]"
                    >
                      {/* 放大镜镜片外圈 */}
                      <circle
                        cx="28"
                        cy="28"
                        r="18"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        fill="none"
                      />
                      {/* 放大镜手柄 */}
                      <line
                        x1="42"
                        y1="42"
                        x2="54"
                        y2="54"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                      />
                      {/* 镜片内装饰 - 表示空白/无内容 */}
                      <circle
                        cx="28"
                        cy="28"
                        r="12"
                        stroke="currentColor"
                        strokeWidth="1"
                        strokeDasharray="3 3"
                        opacity="0.3"
                        fill="none"
                      />
                    </svg>
                  </div>
                </div>
                <h3 className="text-white font-semibold text-lg mb-2">
                  暂无{activeTab === 'interview' ? '排行榜' : '谈薪榜'}数据
                </h3>
                <p className="text-slate-500 text-sm">
                  还没有用户完成
                  {activeTab === 'interview' ? '面试评分' : '谈薪模拟'}
                  ，快来成为第一个上榜的人吧！
                </p>
              </div>
            </div>
          ) : (
            <>
              {/* 前三名展示区 */}
              {activeTab === 'interview'
                ? topThree.length > 0 && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {topThree.map((item, index) => (
                        <TopThreeCard
                          key={item.user_id}
                          item={item}
                          rank={index + 1}
                          index={index}
                        />
                      ))}
                    </div>
                  )
                : salaryTopThree.length > 0 && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {salaryTopThree.map((item, index) => (
                        <TopThreeSalaryCard
                          key={item.id}
                          item={item}
                          rank={index + 1}
                          index={index}
                        />
                      ))}
                    </div>
                  )}

              {/* 其余列表 */}
              {(activeTab === 'interview' ? restList : salaryRestList).length >
                0 && (
                <div className="bg-slate-800/30 backdrop-blur-xl rounded-2xl border border-slate-700/30 p-4">
                  {/* 列表头部 */}
                  <div className="flex items-center justify-between px-4 py-3 mb-4 border-b border-slate-700/30">
                    <div className="flex items-center gap-2">
                      <Medal className="w-4 h-4 text-slate-500" />
                      <span className="text-slate-400 text-sm font-medium">
                        排名
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Target className="w-4 h-4 text-slate-500" />
                      <span className="text-slate-400 text-sm font-medium">
                        综合评分
                      </span>
                    </div>
                  </div>

                  {/* 列表内容 */}
                  <div className="space-y-2">
                    {activeTab === 'interview'
                      ? restList.map((item, index) => (
                          <LeaderboardListItem
                            key={item.user_id}
                            item={item}
                            rank={index + 4}
                            index={index}
                          />
                        ))
                      : salaryRestList.map((item, index) => (
                          <SalaryLeaderboardListItem
                            key={item.id}
                            item={item}
                            rank={index + 4}
                            index={index}
                          />
                        ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* 底部说明 */}
        {!isLoading && !hasError && currentData.length > 0 && (
          <div className="mt-8 text-center">
            <div className="inline-flex items-center gap-6 px-6 py-3 rounded-full bg-slate-800/30 border border-slate-700/30">
              <div className="flex items-center gap-2 text-slate-500 text-sm">
                <Users className="w-4 h-4" />
                <span>共 {currentData.length} 位用户上榜</span>
              </div>
              <div className="w-px h-4 bg-slate-700" />
              <div className="flex items-center gap-2 text-slate-500 text-sm">
                <Sparkles className="w-4 h-4" />
                <span>数据实时更新</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* 自定义动画样式 */}
      <style>{`
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        
        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-5px); }
        }
        
        .animate-shimmer {
          animation: shimmer 2s infinite;
        }
        
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}
